import React, { useRef, useState } from 'react';
import { Typography } from '~/libs/ui/atoms/Typography';
import { Breadcrumbs } from '~/libs/ui/atoms/Breadcrumbs';
import { BreadcrumbsItem } from '~/libs/ui/atoms/BreadcrumbsItem';
import { ProductModuleType } from '~/libs/ui/molecules/ProductModuleType';
import { humanDate } from '~/libs/utils/format/date';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { HiMiniBellAlert, HiOutlineBellSlash } from 'react-icons/hi2';
import { LuDownload, LuHistory, LuSettings } from 'react-icons/lu';
import { RiExchangeDollarLine } from 'react-icons/ri';
import { RateTimePeriod, TimePeriod } from '~/libs/ui/atoms/RateTimePeriod';
import { ProductGroupOptionsPopover } from '~/libs/ui/atoms/ProductGroupOptionsPopover';
import { ProductGroupDownloadPopover } from '~/libs/ui/atoms/ProductGroupDownloadPopover';

interface CustomRates {
	id: string;
	name: string;
	baseCurrency: string;
}

interface RatesSettings {
	type: string;
	name: string;
	date: Date | null;
	customExchangeRates: CustomRates | null;
	timePeriod: TimePeriod;
}

interface HeaderProps {
	group: UserProductGroup;
	currency: string | null;
	name: string;
	lastUpdateTime: Date;
	parentName?: string;
	parentId?: string;
	exchangeRatesSettings: RatesSettings;
	notificationSettingsLink?: string;
	notificationFrequency: string;
	onNotificationModal?(): void;
	onGroupHistoryModal?(): void;
	onExchangeRateModal?(): void;
	onCopy(): void;
	onRename(): void;
	onDelete(): void;
	handleProductsExport(): void;
	handleProductsEvidenceExport(): void;
	handleCzMaxPriceExport(): void;
}

export function Header({
	group,
	currency,
	name,
	lastUpdateTime,
	parentName,
	parentId,
	exchangeRatesSettings,
	notificationSettingsLink,
	notificationFrequency,
	onNotificationModal,
	onGroupHistoryModal,
	onExchangeRateModal,
	onCopy,
	onRename,
	onDelete,
	handleProductsExport,
	handleProductsEvidenceExport,
	handleCzMaxPriceExport,
}: HeaderProps) {
	const downloadBtnRef = useRef<HTMLButtonElement>(null);
	const settingsBtnRef = useRef<HTMLButtonElement>(null);

	const [showDownloadPopup, setShowDownloadPopup] = useState(false);
	const [showSettingsPopup, setShowSettingsPopup] = useState(false);

	const handleShowDownloadPopup = () => {
		setShowSettingsPopup(false);
		setShowDownloadPopup(!showDownloadPopup);
	};

	const handleShowSettingsPopup = () => {
		setShowDownloadPopup(false);
		setShowSettingsPopup(!showSettingsPopup);
	};
	const optionSelected = (action: () => void) => {
		setShowSettingsPopup(false);
		action();
	};

	return (
		<div className='w-full pt-2 pb-3.5 border-b border-gray-200 bg-cBlue-50'>
			<div className='max-w-[1260px] mx-auto px-[40px]'>
				<Breadcrumbs>
					<BreadcrumbsItem link={'/product-groups/#product-list'} firstItem={true}>
						Product List
					</BreadcrumbsItem>
					{parentName && parentId && (
						<BreadcrumbsItem link={`/product-groups/${encodeURIComponent(parentId)}`}>{parentName}</BreadcrumbsItem>
					)}
					<BreadcrumbsItem>{name}</BreadcrumbsItem>
				</Breadcrumbs>

				<div className='mb-3 flex w-full justify-between items-start'>
					<div className='pr-4'>
						<div className='block h-5 mb-1.5 align-top'>
							<ProductModuleType countryCode='CZ' moduleType='Max Price' />
						</div>
						<Typography level={2} weight='font-bold' color='cBlue-800'>
							{name}
						</Typography>
					</div>
					<p className='pt-1.5 text-[12px] text-gray-400 text-right'>
						Last product added/removed on <strong>{humanDate(lastUpdateTime)}</strong>
					</p>
				</div>

				<div className='flex items-start justify-start gap-y-1 w-full'>
					<div className='relative'>
						<button
							ref={downloadBtnRef}
							onClick={handleShowDownloadPopup}
							className={`relative inline-flex items-center mr-4 pl-7 pr-2.5 py-1 rounded border font-semibold leading-4 text-[12px] cursor-pointer tracking-wide ${
								showDownloadPopup
									? 'border-black text-white bg-black'
									: 'border-cPurple-400/40 text-cPurple-600 bg-cPurple-100 bg-opacity-20 transition-colors hover:bg-black hover:border-black hover:text-white'
							}`}
						>
							<LuDownload className='absolute left-1.5 top-1/2 -mt-2 w-4 h-4' />
							Download
						</button>
						{showDownloadPopup && (
							<ProductGroupDownloadPopover
								anchorRef={downloadBtnRef}
								currency={currency}
								onProductsExport={handleProductsExport}
								onProductsEvidenceExport={handleProductsEvidenceExport}
								onCzMaxPriceExport={handleCzMaxPriceExport}
								onClose={handleShowDownloadPopup}
							/>
						)}
					</div>

					{notificationSettingsLink && (
						<button
							onClick={onNotificationModal}
							className={`relative inline-flex items-center mr-1 py-1 px-1.5 rounded border cursor-pointer tracking-wide transition-colors text-center hover:bg-black hover:border-black hover:text-white ${
								notificationFrequency === 'disabled'
									? 'border-gray-400 text-gray-800 bg-white'
									: 'border-green-800/40 text-green-800 bg-green-100'
							}`}
						>
							{notificationFrequency === 'disabled' ? (
								<HiOutlineBellSlash className='w-4 h-4' />
							) : (
								<HiMiniBellAlert className='w-4 h-4' />
							)}
						</button>
					)}

					<div className='relative'>
						<button
							ref={settingsBtnRef}
							onClick={handleShowSettingsPopup}
							className={`relative inline-flex items-center py-1 px-1.5 rounded border font-semibold leading-4 text-[12px] text-center cursor-pointer tracking-wide ${
								showSettingsPopup
									? 'bg-black border-black text-white'
									: 'border-gray-400 text-gray-800 bg-white transition-colors hover:bg-black hover:border-black hover:text-white'
							}`}
						>
							<LuSettings className='w-4 h-4' />
						</button>

						{showSettingsPopup && (
							<ProductGroupOptionsPopover
								anchorRef={settingsBtnRef}
								onRename={() => optionSelected(onRename)}
								onCopy={() => optionSelected(onCopy)}
								onDelete={() => optionSelected(onDelete)}
								onClose={handleShowSettingsPopup}
							/>
						)}
					</div>

					{group.hasProductsHistory() && (
						<button
							onClick={onGroupHistoryModal}
							className='relative inline-flex ml-1 pl-7 pr-2.5 py-1 rounded border border-blue-300 items-center font-semibold leading-4 text-[12px] text-blue-500 bg-white cursor-pointer tracking-wide transition-colors hover:bg-black hover:border-black hover:text-white'
						>
							<LuHistory className='absolute left-1.5 top-1/2 -mt-[9px] w-[18px] h-[18px]' />
							History
						</button>
					)}

					<button
						onClick={onExchangeRateModal}
						className={`relative inline-flex justify-start items-stretch ml-4 rounded border group font-semibold leading-4 text-[12px] cursor-pointer tracking-wide transition-colors uppercase hover:bg-black hover:border-black hover:text-white ${
							exchangeRatesSettings.date
								? 'border-cPurple-700 text-white bg-cPurple-600'
								: 'border-gray-400 text-gray-700 bg-white'
						}`}
					>
						<span className='pl-8 pr-3 py-1'>
							<RiExchangeDollarLine className='absolute left-1.5 top-1/2 -mt-2.5 w-5 h-5' />
							{exchangeRatesSettings.name}
						</span>
						{exchangeRatesSettings.date && (
							<span className='rounded-r py-1 px-2 font-semibold text-black bg-gray-200 transition-colors group-hover:bg-white'>
								<RateTimePeriod
									averageLabel='avg.'
									date={exchangeRatesSettings.date}
									fromLabel=''
									timePeriod={exchangeRatesSettings.timePeriod}
								/>
							</span>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
